import React, { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";

/**
 * ContentProtection — deters casual copying across the entire portal.
 *
 * Techniques applied (all can be disabled via `REACT_APP_CONTENT_PROTECTION=off`):
 *  - CSS: `user-select: none` + image drag disable + print media hidden
 *  - Right-click / context-menu blocked
 *  - Keyboard shortcuts blocked: Ctrl+C, Ctrl+X, Ctrl+A, Ctrl+P, Ctrl+S, Ctrl+U,
 *    F12, Ctrl+Shift+I / J / C
 *  - Image drag & drop suppressed
 *  - DevTools open detection (window-size heuristic) shows a blocking overlay
 *  - Watermark with user email (or "guest") tiled across the viewport (traceability)
 *  - Copyright banner emitted on mount
 *
 * Determined users can still screenshot or retype content — this is a friction
 * layer, not cryptographic protection.
 */

const OFF = String(process.env.REACT_APP_CONTENT_PROTECTION || "").toLowerCase() === "off";

const BLOCKED_KEYS = new Set(["c", "x", "a", "p", "s", "u"]);
const BLOCKED_SHIFT_KEYS = new Set(["i", "j", "c"]);

export default function ContentProtection() {
  const { user } = useAuth();
  const identity = (user && user !== false && user.email) || "guest";

  useEffect(() => {
    if (OFF) return undefined;

    // Add CSS rules that lock down selection and printing.
    const style = document.createElement("style");
    style.setAttribute("data-testid", "content-protection-style");
    style.textContent = `
      html, body { -webkit-user-select: none; -moz-user-select: none; -ms-user-select: none; user-select: none; }
      /* Allow selection inside form fields so users can still type */
      input, textarea, [contenteditable="true"] { -webkit-user-select: text; -moz-user-select: text; -ms-user-select: text; user-select: text; }
      img { -webkit-user-drag: none; user-drag: none; pointer-events: none; }
      /* Ensure images inside interactive containers still respond to clicks */
      a img, button img, [role="button"] img { pointer-events: auto; }
      @media print { body { display: none !important; } }
    `;
    document.head.appendChild(style);

    const onCtx = (e) => e.preventDefault();

    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      // F12
      if (e.key === "F12") { e.preventDefault(); return; }
      // Ctrl / Cmd + key
      if (e.ctrlKey || e.metaKey) {
        if (BLOCKED_KEYS.has(k)) { e.preventDefault(); return; }
        if (e.shiftKey && BLOCKED_SHIFT_KEYS.has(k)) { e.preventDefault(); }
      }
    };

    const onDrag = (e) => {
      if (e.target && ["IMG", "VIDEO", "CANVAS"].includes(e.target.tagName)) e.preventDefault();
    };

    const onCopy = (e) => {
      // Allow copy inside inputs / textareas so users can still copy things they typed
      const t = e.target;
      const editable = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (!editable) e.preventDefault();
    };

    document.addEventListener("contextmenu", onCtx);
    document.addEventListener("keydown", onKey);
    document.addEventListener("dragstart", onDrag);
    document.addEventListener("copy", onCopy);
    document.addEventListener("cut", onCopy);

    // DevTools heuristic — the diff between outerHeight and innerHeight (or Width)
    // spikes when devtools opens as a docked panel. Runs every 1500ms.
    const overlay = document.createElement("div");
    overlay.setAttribute("data-testid", "devtools-block-overlay");
    overlay.style.cssText = [
      "position:fixed", "inset:0", "background:rgba(15,23,42,0.96)",
      "color:#fff", "z-index:2147483647", "display:none",
      "align-items:center", "justify-content:center",
      "text-align:center", "padding:2rem",
      "font-family:'Avenir Next', 'Manrope', system-ui, sans-serif",
    ].join(";");
    // Build via DOM API instead of innerHTML — no injection surface.
    const wrap = document.createElement("div");
    wrap.style.maxWidth = "520px";
    const eyebrow = document.createElement("div");
    eyebrow.style.cssText = "text-transform:uppercase; letter-spacing:0.3em; font-size:11px; opacity:0.7";
    eyebrow.textContent = "Access blocked";
    const heading = document.createElement("h2");
    heading.style.cssText = "font-size:2rem; margin:0.5rem 0 1rem; font-weight:800";
    heading.textContent = "Developer tools are not permitted on this site.";
    const para = document.createElement("p");
    para.style.cssText = "opacity:0.85; line-height:1.5";
    para.textContent = "Arogya Pramana content is protected. Close developer tools to continue.";
    const session = document.createElement("p");
    session.style.cssText = "font-size:11px; opacity:0.55; margin-top:2rem";
    session.textContent = `Session: ${identity}`;
    wrap.append(eyebrow, heading, para, session);
    overlay.appendChild(wrap);
    document.body.appendChild(overlay);

    const check = () => {
      const threshold = 160;
      const heightDiff = window.outerHeight - window.innerHeight;
      const widthDiff = window.outerWidth - window.innerWidth;
      const open = heightDiff > threshold || widthDiff > threshold;
      overlay.style.display = open ? "flex" : "none";
    };
    check();
    const iv = setInterval(check, 1500);
    window.addEventListener("resize", check);

    console.info("%c© Arogya Pramana", "font-size:16px;color:#0b3d3a;font-weight:700", "All content is proprietary. Reproduction, redistribution, or unauthorised scraping is prohibited.");

    return () => {
      document.removeEventListener("contextmenu", onCtx);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("dragstart", onDrag);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("cut", onCopy);
      window.removeEventListener("resize", check);
      clearInterval(iv);
      style.remove();
      overlay.remove();
    };
  }, [identity]);

  return null;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (m) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[m]));
}
